import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  Tab,
  Tabs,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import Header from "@app/components/header";
import Footer from "@app/components/footer";
import { useNavigate } from "react-router-dom";
import { Test } from "@app/models/test.model";
import useObservable from "@core/hooks/use-observable.hook";
import TestService, { TestPaginationOption } from "@core/services/http/test.service";
import ExamService from "@core/services/http/exam.service";
import { useDispatch } from "react-redux";
import { ExamAttemptActionType } from "@app/store/exam-attempt/exam-attempt.type";

const examCategories = [
  "Tất cả",
  "IELTS Academic",
  "IELTS General",
  "TOEIC",
  "HSK 1",
  "HSK 2",
  "HSK 3",
  "HSK 4",
  "HSK 5",
  "HSK 6",
  "TOPIK I",
  "TOPIK II",
  "Toán THPTQG",
  "Tiếng Anh THPTQG",
  "Sinh học THPTQG",
  "Hóa học THPTQG",
  "Vật lý THPTQG",
  "NEW SAT",
  "ACT",
];

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: "#f5f5f5",
  minHeight: "calc(100vh - 64px)",
  maxWidth: "1200px",
  margin: "0 auto",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const PageWrapper = styled(Box)({
  backgroundColor: "#f5f5f5",
  minHeight: "calc(100vh - 64px)",
});

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: "24px",
  fontWeight: 500,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
    marginBottom: theme.spacing(2),
  },
}));

const SearchBox = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

const SearchInput = styled(TextField)(() => ({
  flex: 1,
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
  },
}));

const SearchButton = styled(Button)(({ theme }) => ({
  minWidth: "120px",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const TabsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "4px",
  padding: theme.spacing(1),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.5),
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: "36px",
  "& .MuiTab-root": {
    minHeight: "36px",
    padding: "6px 16px",
    textTransform: "none",
    fontSize: "14px",
    [theme.breakpoints.down("sm")]: {
      padding: "6px 12px",
      fontSize: "13px",
    },
  },
}));

const TestsContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
  gap: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(2),
  },
}));

const TestCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2),
  height: "100%",
  backgroundColor: "#fff",
  borderRadius: "4px",
}));

const TestTitle = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 500,
  marginBottom: theme.spacing(2),
  color: "#1a73e8",
}));

const TestStats = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(1),
  color: "#666",
  fontSize: "14px",
  alignItems: "center",
}));

const DetailButton = styled(Button)({
  textTransform: "none",
  color: "#1a73e8",
  backgroundColor: "#fff",
  border: "1px solid #1a73e8",
  "&:hover": {
    backgroundColor: "#f8f9fa",
  },
});

function HomePage() {
  const { subscribeOnce } = useObservable();

  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedSubTab, setSelectedSubTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [total, setTotal] = useState(0);
  const [tests, setTests] = useState<Test[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pagination, setPagination] = useState(() => {
    const options: TestPaginationOption = {
      // ...DEFAULT_PAGINATION_OPTION,
      page: 0,
      limit: 20,
    };

    return options;
  });
  const navigate = useNavigate();

  useEffect(() => {
    subscribeOnce(TestService.getList(pagination), (response) => {
      setTests(response?.data as Test[]);
      setTotal(response?.pagination?.total || 0);
    });
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleSubTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setSelectedSubTab(newValue);
  };

  const handleStartTest = (testId: number) => {
    const test = tests.find((test) => test.id === testId);

    if (test) {
      subscribeOnce(ExamService.create(testId, test.name), (response) => {
        dispatch({
          type: ExamAttemptActionType.SET_CURRENT_ATTEMPT,
          payload: {
            attemptId: response.examAttempt.id,
          },
        });
        navigate(`/exam/${testId}`);
      });
    }
  };

  return (
    <>
      <Header />
      <PageWrapper>
        <PageContainer>
          <PageTitle>Thư viện đề thi</PageTitle>

          <SearchBox>
            <SearchInput
              fullWidth
              placeholder="Nhập từ khóa bạn muốn tìm kiếm: tên sách, dạng câu hỏi ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <SearchButton variant="contained" color="primary">
              Tìm kiếm
            </SearchButton>
          </SearchBox>

          <TabsContainer>
            <StyledTabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {examCategories.map((category) => (
                <Tab key={category} label={category} />
              ))}
            </StyledTabs>
          </TabsContainer>

          <Box sx={{ mb: 2 }}>
            <Tabs value={selectedSubTab} onChange={handleSubTabChange}>
              <Tab label="Tất cả" />
              <Tab label="Đề rút gọn" />
            </Tabs>
          </Box>

          <TestsContainer>
            {tests.map((test) => (
              <TestCard key={test.id}>
                <TestTitle>{test.name}</TestTitle>
                <TestStats>
                  {/* <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 16 }} />
                    {exam.duration}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PeopleIcon sx={{ fontSize: 16 }} />
                    {exam.periods.reduce(
                      (acc, period) =>
                        acc +
                        period.results.reduce(
                          (acc, result) => acc + result.attempt_number,
                          0
                        ),
                      0
                    )}
                  </Box> */}
                  {/* <Box>| {exam.views}</Box> */}
                </TestStats>
                {/* <TestInfo>
                  <Typography>{exam.sections}</Typography>
                  <Typography>{exam.questions}</Typography>
                </TestInfo> */}
                {/* <TagsContainer>
                  {exam.tags.map((tag, index) => (
                    <Tag key={index}>{tag}</Tag>
                  ))}
                </TagsContainer> */}
                <DetailButton
                  fullWidth
                  onClick={() => handleStartTest(test.id)}
                >
                  Làm bài thi
                </DetailButton>
              </TestCard>
            ))}
          </TestsContainer>
        </PageContainer>
      </PageWrapper>
      <Footer />
    </>
  );
}

export default HomePage;
